import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { customEntriesAPI, jobsAPI, clientsAPI, usersAPI, timesheetsAPI } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import CustomEntryAttachments from '../components/CustomEntryAttachments';
import { formatDate, formatDateShort } from '../utils/dateUtils';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Loader2,
  AlertCircle,
  DollarSign,
  Paperclip
} from 'lucide-react';

const CustomTimesheetEntryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Data
  const [subcontractors, setSubcontractors] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState(null);

  // Form state
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedWeekStart, setSelectedWeekStart] = useState(null);
  const [comments, setComments] = useState('');
  const [entries, setEntries] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isChargeable, setIsChargeable] = useState(true);  // Whether to include in client billing
  
  // Ref for attachments component
  const attachmentsRef = useRef(null);

  // Generate past weeks for selection (back to Nov 23, 2025)
  const getPastWeeks = () => {
    const weeks = [];
    const today = new Date();
    
    const currentSunday = new Date(today);
    const dayOfWeek = currentSunday.getDay();
    currentSunday.setDate(currentSunday.getDate() - dayOfWeek);
    currentSunday.setHours(12, 0, 0, 0);
    
    // Target date: Sunday, November 23, 2025
    const targetDate = new Date(2025, 10, 23, 12, 0, 0); // Month is 0-indexed, so 10 = November
    
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    // Calculate number of weeks from target date to current Sunday
    const msPerWeek = 7 * 24 * 60 * 60 * 1000;
    const weeksDiff = Math.ceil((currentSunday.getTime() - targetDate.getTime()) / msPerWeek);
    const numWeeks = Math.max(weeksDiff + 1, 12); // At least 12 weeks, or more to reach target date
    
    for (let i = 0; i < numWeeks; i++) {
      const weekStart = new Date(currentSunday);
      weekStart.setDate(weekStart.getDate() - (i * 7));
      
      // Stop if we've gone past the target date
      if (weekStart < targetDate) break;
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      const startStr = formatLocalDate(weekStart);
      const endStr = formatLocalDate(weekEnd);
      
      weeks.push({
        start: startStr,
        end: endStr,
        label: `${formatDateShort(startStr)} - ${formatDateShort(endStr)}${i === 0 ? ' (Current Week)' : ''}`
      });
    }
    
    return weeks;
  };

  const pastWeeks = getPastWeeks();

  // Get dates for selected week
  const getWeekDates = () => {
    const weekData = pastWeeks.find(w => w.start === selectedWeekStart);
    if (!weekData) return [];
    
    const dates = [];
    const start = new Date(weekData.start + 'T12:00:00');
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(date.getDate() + i);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
    }
    
    return dates;
  };

  const weekDates = getWeekDates();

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/timesheets');
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [jobsRes, clientsRes, periodRes, subRes] = await Promise.all([
        jobsAPI.getActive(),
        clientsAPI.getAll(),
        timesheetsAPI.getCurrentPeriod(),
        usersAPI.getSubcontractors()
      ]);

      setAllJobs(jobsRes.data);
      setClients(clientsRes.data);
      setCurrentPeriod(periodRes.data);
      // Sort subcontractors alphabetically
      const sortedSubs = subRes.data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setSubcontractors(sortedSubs);

      if (!isNew) {
        const entryRes = await customEntriesAPI.getById(id);
        const entry = entryRes.data;
        setSelectedUserId(entry.user_id);
        setSelectedJob(entry.job_id);
        setSelectedWeekStart(entry.period_start);
        setComments(entry.comments || '');
        setEntries(entry.entries || []);
        setAttachments(entry.attachments || []);
        setIsChargeable(entry.is_chargeable !== false);  // Default to true if not set
        
        // Load available jobs for this user
        await loadAvailableJobs(entry.user_id, jobsRes.data);
      } else {
        setSelectedWeekStart(periodRes.data.start_date);
        
        // Reset comments, attachments, and chargeable flag for new custom entries
        setComments('');
        setAttachments([]);
        setIsChargeable(true);  // Default to chargeable
        
        // Calculate Monday's date (start_date is Sunday, so add 1 day)
        const sundayDate = new Date(periodRes.data.start_date + 'T12:00:00');
        const mondayDate = new Date(sundayDate);
        mondayDate.setDate(sundayDate.getDate() + 1);
        const mondayDateStr = mondayDate.toISOString().split('T')[0];
        
        setEntries([{
          id: Date.now().toString(),
          date: mondayDateStr,
          description: '',
          quantity: 1,
          value: 0
        }]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableJobs = async (userId, allJobsList) => {
    try {
      const availableRes = await usersAPI.getAvailableJobs(userId);
      const availableIds = availableRes.data;
      
      if (availableIds.length > 0) {
        const filteredJobs = allJobsList.filter(job => availableIds.includes(job.id));
        setJobs(filteredJobs);
      } else {
        setJobs([]);
      }
    } catch (err) {
      console.error('Failed to load available jobs:', err);
      setJobs(allJobsList);
    }
  };

  const handleUserChange = async (newUserId) => {
    setSelectedUserId(newUserId);
    setSelectedJob('');
    
    if (newUserId) {
      await loadAvailableJobs(newUserId, allJobs);
    } else {
      setJobs([]);
    }
  };

  const handleWeekChange = (weekStart) => {
    setSelectedWeekStart(weekStart);
    // Update entry dates to be within the new week, starting from Monday
    const weekData = pastWeeks.find(w => w.start === weekStart);
    if (weekData && entries.length > 0) {
      const sundayDate = new Date(weekStart + 'T12:00:00');
      setEntries(entries.map((e, index) => {
        // Calculate the date for each entry based on its position (starting from Monday)
        const entryDate = new Date(sundayDate);
        entryDate.setDate(sundayDate.getDate() + 1 + index); // +1 for Monday, +index for subsequent days
        return {
          ...e,
          date: entryDate.toISOString().split('T')[0]
        };
      }));
    }
  };

  const addEntry = () => {
    // Get all dates already used by existing entries
    const usedDates = new Set(entries.map(e => e.date));
    
    // Generate all week dates in order (Sunday to Saturday)
    const getWeekDatesArray = () => {
      if (!selectedWeekStart) return [];
      const startDate = new Date(selectedWeekStart + 'T12:00:00');
      const dates = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        dates.push(date.toISOString().split('T')[0]);
      }
      return dates;
    };
    
    const allWeekDates = getWeekDatesArray();
    
    // Find the next sequential date after the last entry
    let nextDate = '';
    
    if (entries.length === 0) {
      // No entries yet - start with Monday (second day of the week, index 1)
      nextDate = allWeekDates[1] || allWeekDates[0] || selectedWeekStart || '';
    } else {
      // Find the latest date used in entries
      const entryDates = entries.map(e => e.date).filter(d => d);
      const sortedEntryDates = entryDates.sort();
      const lastDate = sortedEntryDates[sortedEntryDates.length - 1];
      
      // Find the index of the last date in the week
      const lastDateIndex = allWeekDates.indexOf(lastDate);
      
      if (lastDateIndex >= 0 && lastDateIndex < 6) {
        // Try the next day in sequence
        const nextDayIndex = lastDateIndex + 1;
        const nextDayDate = allWeekDates[nextDayIndex];
        
        if (!usedDates.has(nextDayDate)) {
          nextDate = nextDayDate;
        } else {
          // Next sequential day is used, find the next available day after that
          for (let i = nextDayIndex + 1; i < 7; i++) {
            if (!usedDates.has(allWeekDates[i])) {
              nextDate = allWeekDates[i];
              break;
            }
          }
        }
      }
      
      // If still no date found, look for any unused date from the beginning
      if (!nextDate) {
        nextDate = allWeekDates.find(d => !usedDates.has(d)) || '';
      }
    }
    
    // If no unused date found, fallback to weekStart (edge case)
    if (!nextDate) {
      nextDate = selectedWeekStart || '';
    }
    
    setEntries([...entries, {
      id: Date.now().toString(),
      date: nextDate,
      description: '',
      quantity: 1,
      value: 0
    }]);
  };

  const removeEntry = (entryId) => {
    setEntries(entries.filter(e => e.id !== entryId));
  };

  const updateEntry = (entryId, field, value) => {
    setEntries(entries.map(e => 
      e.id === entryId ? { ...e, [field]: value } : e
    ));
  };

  const handleSave = async () => {
    if (!selectedUserId) {
      setError('Please select a subcontractor');
      return;
    }
    if (!selectedJob) {
      setError('Please select a job');
      return;
    }
    if (entries.length === 0) {
      setError('Please add at least one entry');
      return;
    }

    const weekData = pastWeeks.find(w => w.start === selectedWeekStart);

    setSaving(true);
    setError('');

    try {
      const data = {
        user_id: selectedUserId,
        job_id: selectedJob,
        period_start: selectedWeekStart,
        period_end: weekData?.end || selectedWeekStart,
        comments: comments,
        is_chargeable: isChargeable,
        entries: entries.map(({ id, ...e }) => ({
          ...e,
          quantity: parseFloat(e.quantity) || 0,
          value: parseFloat(e.value) || 0
        }))
      };

      if (isNew) {
        const response = await customEntriesAPI.create(data);
        const newEntryId = response.data.id;
        
        // Upload any pending attachments
        if (attachmentsRef.current) {
          const pendingFiles = attachmentsRef.current.getPendingFiles();
          if (pendingFiles && pendingFiles.length > 0) {
            await attachmentsRef.current.uploadPendingFiles(newEntryId);
          }
        }
        
        toast.success('Custom entry created');
        navigate('/timesheets');
      } else {
        await customEntriesAPI.update(id, data);
        toast.success('Custom entry updated');
        navigate('/timesheets');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save custom entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this custom entry?')) return;
    
    try {
      await customEntriesAPI.delete(id);
      toast.success('Custom entry deleted');
      navigate('/timesheets');
    } catch (error) {
      toast.error('Failed to delete custom entry');
    }
  };

  // Calculate totals - multiply quantity × value for each entry
  const totalValue = entries.reduce((sum, e) => sum + ((parseFloat(e.quantity) || 0) * (parseFloat(e.value) || 0)), 0);
  const totalQty = entries.reduce((sum, e) => sum + (parseFloat(e.quantity) || 0), 0);

  // Get selected job and client
  const selectedJobData = allJobs.find(j => j.id === selectedJob);
  const selectedClient = selectedJobData ? clients.find(c => c.id === selectedJobData.client_id) : null;

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#194eb2]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/timesheets')}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {isNew ? 'New Custom Entry' : 'Edit Custom Entry'}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Manual value entry for work not captured by standard rates
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isNew && (
              <button
                onClick={handleDelete}
                className="btn-secondary text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex items-center gap-2"
              data-testid="save-custom-entry-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving...' : 'Save Entry'}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Header Info */}
        <div className="card-base">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-slate-900">Entry Details</h2>
          </div>
          <div className="card-body grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Subcontractor <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedUserId}
                onChange={(e) => handleUserChange(e.target.value)}
                className="input-base"
                data-testid="user-select"
              >
                <option value="">Select subcontractor...</option>
                {[...subcontractors].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Project <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedJob}
                onChange={(e) => setSelectedJob(e.target.value)}
                disabled={jobs.length === 0}
                className="input-base"
                data-testid="job-select"
              >
                <option value="">Select a job...</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.job_number} - {job.name}
                  </option>
                ))}
              </select>
              {selectedUserId && jobs.length === 0 && (
                <p className="mt-1 text-xs text-amber-600">
                  No jobs configured for this user. Set up pay rates in Users → Manage Rates.
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Client</label>
              <input
                type="text"
                value={selectedClient?.name || '-'}
                disabled
                className="input-base bg-slate-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Weekly Period</label>
              <select
                value={selectedWeekStart || ''}
                onChange={(e) => handleWeekChange(e.target.value)}
                className="input-base"
                data-testid="week-select"
              >
                {pastWeeks.map((week) => (
                  <option key={week.start} value={week.start}>
                    {week.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Custom Entries */}
        <div className="card-base">
          <div className="card-header flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Value Entries</h2>
            <button
              onClick={addEntry}
              className="btn-secondary text-sm flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Entry
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="table-header">
                <tr>
                  <th className="px-4 py-3 text-left w-40">Date</th>
                  <th className="px-4 py-3 text-left">Description</th>
                  <th className="px-4 py-3 text-center w-24">Qty</th>
                  <th className="px-4 py-3 text-right w-32">Value ($)</th>
                  <th className="px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                      No entries. Click "Add Entry" to begin.
                    </td>
                  </tr>
                ) : (
                  entries.map((entry) => (
                    <tr key={entry.id} className="table-row">
                      <td className="px-4 py-3">
                        <select
                          value={entry.date}
                          onChange={(e) => updateEntry(entry.id, 'date', e.target.value)}
                          className="input-base text-sm"
                        >
                          {weekDates.map(date => (
                            <option key={date} value={date}>
                              {formatDateShort(date)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={entry.description}
                          onChange={(e) => updateEntry(entry.id, 'description', e.target.value)}
                          placeholder="Enter description..."
                          className="input-base text-sm"
                          data-testid="entry-description"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          value={entry.quantity}
                          onChange={(e) => updateEntry(entry.id, 'quantity', e.target.value)}
                          min="0"
                          step="0.5"
                          className="input-base text-sm text-center font-mono w-20 mx-auto"
                          data-testid="entry-qty"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="number"
                            value={entry.value}
                            onChange={(e) => updateEntry(entry.id, 'value', e.target.value)}
                            min="0"
                            step="0.01"
                            className="input-base text-sm text-right font-mono pl-8"
                            data-testid="entry-value"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {entries.length > 1 && (
                          <button
                            onClick={() => removeEntry(entry.id)}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {entries.length > 0 && (
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr>
                    <td colSpan={2} className="px-4 py-3 text-right font-medium text-slate-700">
                      Totals:
                    </td>
                    <td className="px-4 py-3 text-center font-mono font-medium text-slate-900">
                      {totalQty}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-[#194eb2]">
                      ${totalValue.toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Comments */}
        <div className="card-base">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-slate-900">Comments</h2>
          </div>
          <div className="card-body">
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Add any comments for this custom entry (optional)..."
              rows={3}
              className="input-base"
            />
          </div>
        </div>

        {/* Chargeable to Client */}
        <div className="card-base">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-slate-900">Chargeable to Client</h2>
          </div>
          <div className="card-body">
            <div className="flex items-center gap-4">
              <select
                value={isChargeable ? 'yes' : 'no'}
                onChange={(e) => setIsChargeable(e.target.value === 'yes')}
                className="input-base w-48"
                data-testid="is-chargeable-select"
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              <p className="text-sm text-slate-500">
                {isChargeable 
                  ? 'This entry will be included in client billing and invoice generation.'
                  : 'This entry will NOT be included in client billing or invoice generation.'}
              </p>
            </div>
          </div>
        </div>

        {/* Attachments */}
        <div className="card-base">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Paperclip className="w-5 h-5" />
              Attachments
            </h2>
          </div>
          <div className="card-body">
            <CustomEntryAttachments
              ref={attachmentsRef}
              customEntryId={id}
              attachments={attachments}
              onAttachmentsChange={setAttachments}
              readOnly={false}
              isNew={isNew}
            />
          </div>
        </div>

        {/* Info Box */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <DollarSign className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800">About Custom Entries</p>
              <p className="text-sm text-blue-600 mt-1">
                Custom entries are used for work not captured by standard job rates. 
                The value entered will be included in estimated costs for subcontractor payments 
                and client billing calculations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CustomTimesheetEntryPage;
